import { Check, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

const TIER_ORDER = { free: 0, professional: 1, executive: 2, elite: 3 };

function formatPrice(cents, t) {
  if (!cents) return t("common.free");
  const dollars = (cents / 100).toFixed(2).replace(/\.00$/, "");
  return `$${dollars}`;
}

export default function TierCard({ tier, currentTier, onPick }) {
  const { t } = useTranslation();
  const isCurrent = tier.id === currentTier;
  const isUpgrade = TIER_ORDER[tier.id] > TIER_ORDER[currentTier];
  const isDowngrade = TIER_ORDER[tier.id] < TIER_ORDER[currentTier];
  const isElite = tier.id === "elite";

  let cta = null;
  if (isCurrent) {
    cta = (
      <button
        type="button"
        disabled
        data-testid={`tier-cta-${tier.id}`}
        className="w-full h-10 rounded-full text-[12.5px] font-semibold border border-[rgba(0,229,255,0.5)] text-[#00E5FF] bg-[rgba(0,229,255,0.08)] inline-flex items-center justify-center gap-1.5"
      >
        <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
        {t("common.current_plan")}
      </button>
    );
  } else if (tier.id === "free" && currentTier === "free") {
    cta = (
      <button
        type="button"
        disabled
        data-testid={`tier-cta-${tier.id}`}
        className="w-full h-10 rounded-full text-[12.5px] font-semibold border border-white/[0.1] text-[#9CA3AF] bg-white/[0.02]"
      >
        {t("common.continue_free")}
      </button>
    );
  } else if (tier.id === "free") {
    cta = (
      <button
        type="button"
        onClick={() => onPick(tier)}
        data-testid={`tier-cta-${tier.id}`}
        className="w-full h-10 rounded-full text-[12.5px] border border-white/[0.1] text-[#E5E7EB] bg-white/[0.04] hover:bg-white/[0.08]"
      >
        {t("common.downgrade")}
      </button>
    );
  } else {
    cta = (
      <button
        type="button"
        onClick={() => onPick(tier)}
        data-testid={`tier-cta-${tier.id}`}
        className={`
          w-full h-10 rounded-full text-[12.5px] font-semibold inline-flex items-center justify-center gap-1.5
          ${isUpgrade
            ? "bg-[rgba(0,229,255,0.15)] border border-[rgba(0,229,255,0.55)] text-[#00E5FF] hover:bg-[rgba(0,229,255,0.22)] shadow-[0_0_16px_rgba(0,229,255,0.3)]"
            : "border border-white/[0.1] text-[#E5E7EB] bg-white/[0.04] hover:bg-white/[0.08]"}
        `}
      >
        {isUpgrade ? t("common.upgrade") : t("common.downgrade")}
      </button>
    );
  }

  return (
    <div
      data-testid={`tier-card-${tier.id}`}
      className={`
        relative rounded-2xl p-4 flex flex-col gap-3
        ${isElite
          ? "bg-[rgba(0,229,255,0.04)] border border-[rgba(0,229,255,0.45)] shadow-[0_0_28px_-10px_rgba(0,229,255,0.5)]"
          : "bg-white/[0.025] border border-white/[0.07]"}
      `}
    >
      {/* Phase 9: Most Popular badge on Executive — shown for ALL users,
          coexists with `current_plan` indicator. */}
      {tier.id === "executive" ? (
        <span
          data-testid="tier-most-popular-badge"
          className="
            absolute -top-3 right-4 z-10
            text-[10.5px] uppercase tracking-[0.18em] font-semibold
            px-3 py-1 rounded-full
            bg-[rgba(0,229,255,0.22)]
            border border-[rgba(0,229,255,0.55)]
            text-[#00E5FF]
            shadow-[0_0_18px_rgba(0,229,255,0.45)]
          "
        >
          {t("billing.tier.most_popular")}
        </span>
      ) : null}
      {isElite ? (
        <span className="absolute -top-2 right-4 text-[9.5px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full bg-[rgba(0,229,255,0.18)] border border-[rgba(0,229,255,0.55)] text-[#00E5FF]">
          <Sparkles className="h-3 w-3 inline mr-1" strokeWidth={2} />
          {t("billing.tiers.elite_badge")}
        </span>
      ) : null}
      <div>
        <p className="text-[10.5px] uppercase tracking-[0.18em] text-[#9CA3AF]">
          {t(`billing.tier.${tier.id}.name`, tier.display_name)}
        </p>
        <p
          data-testid={`tier-price-${tier.id}`}
          className="mt-1 text-[24px] font-semibold text-[#E5E7EB] font-mono leading-none"
        >
          {formatPrice(tier.price_cents, t)}
          {tier.price_cents ? (
            <span className="text-[12px] font-normal text-[#9CA3AF] ml-1">
              / {t("common.month")}
            </span>
          ) : null}
        </p>
        <p className="mt-1 text-[11.5px] text-[#9CA3AF]">
          {t(`billing.tier.${tier.id}.tagline`, "")}
        </p>
      </div>
      <ul className="flex flex-col gap-1.5 text-[12px] text-[#E5E7EB]/90 mt-1 flex-1">
        {(tier.features || []).map((f) => {
          // Backend returns i18n keys like "billing.tier.features.free.0" so
          // the translated copy comes from the active locale. If the key is
          // missing, i18next falls back to the key itself — we detect that
          // by checking the dot-prefix and hide the raw key instead.
          const isKey = typeof f === "string" && f.startsWith("billing.tier.features.");
          const label = isKey ? t(f, { defaultValue: "" }) : f;
          if (!label) return null;
          return (
            <li key={f} className="flex items-start gap-1.5 leading-snug">
              <Check
                className="h-3.5 w-3.5 text-[#00E5FF] shrink-0 mt-0.5"
                strokeWidth={2.5}
              />
              <span>{label}</span>
            </li>
          );
        })}
      </ul>
      {cta}
    </div>
  );
}
